import { useAuditUndo, useAuditUndoPreview } from "@api/generated/audit/audit";
import type { SeasonStepKey } from "@api/generated/model";
import { Icon } from "@app/icons";
import { stepOf } from "@app/seasonCycle";
import { Button, Loader } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { ConfirmModal } from "@shell/Dialogs";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useRouterState } from "@tanstack/react-router";
import { useTableText } from "@ui/audit";
import { formatNumber } from "@ui/format";
import { NextStepButton } from "@ui/SeasonStep";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

/** Undo one operation after showing what it takes back (or why it cannot: rows changed since). Every query is read
 *  again afterwards: an operation can have written any table. ``onDone`` runs after a successful undo. */
export function UndoDialog({ opId, title, onClose, onDone }: { opId: string; title: ReactNode; onClose: () => void; onDone?: () => void }) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const preview = useAuditUndoPreview(opId);
  const undo = useAuditUndo({
    mutation: {
      onSuccess: () => {
        notifications.show({ color: "green", message: t("journal.undoDone") });
        void client.invalidateQueries();
        onDone?.();
        onClose();
      },
    },
  });
  const tableText = useTableText();
  const p = preview.data;
  const tables = p
    ? Object.entries(p.tables)
        .map(([k, v]) => `${tableText(k)}: ${formatNumber(v)}`)
        .join(", ")
    : "";
  return (
    <ConfirmModal
      title={t("journal.undoTitle")}
      confirm={t("journal.undo")}
      danger
      disabled={!p || !!p.conflicts?.length}
      loading={undo.isPending}
      onConfirm={() => undo.mutate({ opId })}
      onClose={onClose}
    >
      <b>{title}</b>
      {!p ? (
        <Loader size="sm" />
      ) : p.conflicts?.length ? (
        <div className="note err">{t("journal.undoBlocked", { count: p.conflicts.length })}</div>
      ) : (
        <span className="sm">{t("journal.undoConfirm", { count: formatNumber(p.changes), tables: tables })}</span>
      )}
    </ConfirmModal>
  );
}

/** What a workflow run did, where its run bar was (docs/ui-design.md): the result in words, "Visszavonás" (the
 *  run's operation, through UndoDialog), the page's own links (downloads, "open"), the Napló, and the next step of
 *  the season cycle — after the page's own step, or ``after`` for a page outside the cycle (null: none). After an
 *  undo it says so and offers nothing more; ``onUndone`` resets the page's own state. */
export function ResultBanner({
  children,
  operation,
  onUndone,
  actions,
  after,
  details,
}: {
  children: ReactNode;
  operation?: string | null;
  onUndone?: () => void;
  actions?: ReactNode;
  after?: SeasonStepKey | null;
  /** What the run produced beside the banner (its documents): gone after an undo. */
  details?: ReactNode;
}) {
  const { t } = useTranslation();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const step = after === null ? undefined : (after ?? stepOf(pathname));
  const [asking, setAsking] = useState(false);
  const [undone, setUndone] = useState(false);
  if (undone) {
    return (
      <section className="result-banner undone" role="status">
        <Icon name="undo" size={16} />
        <span className="grow">{t("result.undone")}</span>
      </section>
    );
  }
  return (
    <>
      <section className="result-banner" role="status">
        <span className="result-mark">
          <Icon name="check" size={15} strokeWidth={2.6} />
        </span>
        <span className="grow">{children}</span>
        {operation && (
          <Button size="xs" variant="default" leftSection={<Icon name="undo" size={14} />} onClick={() => setAsking(true)}>
            {t("journal.undo")}
          </Button>
        )}
        {actions}
        <Link to="/system/journal" search={{ tab: "operations" }} className="sm">
          {t("result.journal")}
        </Link>
        {step && <NextStepButton after={step} />}
        {asking && operation && (
          <UndoDialog
            opId={operation}
            title={children}
            onClose={() => setAsking(false)}
            onDone={() => {
              setUndone(true);
              onUndone?.();
            }}
          />
        )}
      </section>
      {details}
    </>
  );
}
